import { useEffect, useState } from 'react';

import * as Amplitude from '@amplitude/analytics-browser';
import type { ValidPropertyType } from '@amplitude/analytics-types';

import { appConfig } from '@ll-platform/frontend/config/app.config';
import { useAnalyticsCustomIdentifyContext } from '@ll-platform/frontend/core/analytics/AnalyticsCustomIdentifyContext';
import {
  activityTracker,
  analyticsDebugLog,
} from '@ll-platform/frontend/core/analytics/activityTracker';
import {
  buildEventName,
  isEventOnChannel,
} from '@ll-platform/frontend/core/analytics/helpers';
import {
  EventChannel,
  type TrackingEvent,
} from '@ll-platform/frontend/core/analytics/types';
import { updateAmplitudeUserProperty } from '@ll-platform/frontend/core/analytics/updateAmplitudeUserProperty';
import type { ActiveUserProfile } from '@ll-platform/frontend/core/auth/types';
import { useOptionalActiveOrganization } from '@ll-platform/frontend/core/organization/ActiveOrganizationProvider';

const amplitudeSegmentSeparator = ' ';

export const useAmplitude = (activeUser?: ActiveUserProfile) => {
  const [client] = useState(() => {
    const key = appConfig.REACT_APP_AMPLITUDE_KEY;
    if (!key) {
      return;
    }

    analyticsDebugLog(`Initializing Amplitude`);

    const instance = Amplitude.init(key, {
      logLevel: Amplitude.Types.LogLevel.Warn,
    });

    return instance;
  });
  const isClientFalsy = !client;

  const { activeOrganization } = useOptionalActiveOrganization();
  const { contexts } = useAnalyticsCustomIdentifyContext();

  useEffect(() => {
    if (isClientFalsy) {
      return;
    }
    if (activeUser) {
      analyticsDebugLog(
        `Identifying user ${activeUser._id} ${activeUser.email} in Amplitude`,
      );
      Amplitude.identify(new Amplitude.Identify());
      Amplitude.setUserId(activeUser._id);
      updateAmplitudeUserProperty([
        { property: 'email', value: activeUser.email },
        { property: 'accountType', value: activeUser.accountType },
        { property: 'isActive', value: activeUser.isActive },
      ]);
      if (activeOrganization) {
        analyticsDebugLog(
          `Identifying organization ${activeOrganization.id} ${activeOrganization.name} in Amplitude`,
        );
        updateAmplitudeUserProperty([
          { property: 'organizationId', value: activeOrganization.id },
          { property: 'organizationName', value: activeOrganization.name },
          {
            property: 'organizationDomain',
            value: activeOrganization.domain ?? '',
          },
        ]);
      }
    } else {
      analyticsDebugLog(`Deidentifying user in Amplitude`);
      Amplitude.setUserId(undefined);
      Amplitude.identify(new Amplitude.Identify());
    }
  }, [isClientFalsy, activeUser, activeOrganization]);

  useEffect(() => {
    if (!client) {
      return;
    }

    contexts.forEach((contextValue, contextType) => {
      analyticsDebugLog(
        `Identifying group ${contextType}:${contextValue.key} in Amplitude`,
      );

      Amplitude.setGroup(contextType, contextValue.key);
      const groupIdentifyEvent = new Amplitude.Identify();
      Object.entries(contextValue).forEach(([property, value]) => {
        groupIdentifyEvent.set(property, value as ValidPropertyType);
      });
      Amplitude.groupIdentify(
        contextType,
        contextValue.key,
        groupIdentifyEvent,
      );
    });

    return () => {
      contexts.forEach((_, contextType) => {
        analyticsDebugLog(`Clearing group ${contextType} in Amplitude`);
        // There is no method to unset a group without resetting entire client
        // Setting to empty array is not reliable
        Amplitude.setGroup(contextType, '(none)');
      });
    };
  }, [client, contexts]);

  useEffect(() => {
    if (!client) {
      return;
    }

    return activityTracker.subscribe('Amplitude', (event: TrackingEvent) => {
      // amplitude processes events on analytics channel
      if (!isEventOnChannel(event, EventChannel.Analytics)) {
        return;
      }
      Amplitude.track(
        buildEventName(event.type, amplitudeSegmentSeparator),
        event.metadata,
      );
    });
  }, [client]);
};
